import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Box,
  Typography,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import { toast } from 'react-toastify';
import { lookupCompanyByVAT, addCustomer } from '../services/customerService';

/**
 * Reusable Add Customer Dialog Component
 * @param {boolean} open - Controls dialog visibility
 * @param {function} onClose - Callback when dialog is closed
 * @param {function} onCustomerCreated - Callback with created customer data
 * @param {object} optionalFields - Define which fields are optional: { vat, email, phone, peppol }
 * @param {boolean} hidePeppol - Hide Peppol ID field completely (for delivery notes)
 */
const AddCustomerDialog = ({ open, onClose, onCustomerCreated, optionalFields = {}, hidePeppol = false }) => {
  const { t } = useTranslation();

  // Internal state management
  const [newCustomerData, setNewCustomerData] = useState({
    vat: '',
    companyName: '',
    customerName: '',
    email: '',
    phoneNumber: '',
    street: '',
    box: '',
    postCode: '',
    city: '',
    country: 'Belgium',
    peppolId: '',
    isActive: 'true'
  });
  const [customerErrors, setCustomerErrors] = useState({});
  const [vatSearching, setVatSearching] = useState(false);

  // Handle dialog close - reset form
  const handleClose = () => {
    setNewCustomerData({
      vat: '',
      companyName: '',
      customerName: '',
      email: '',
      phoneNumber: '',
      street: '',
      box: '',
      postCode: '',
      city: '',
      country: 'Belgium',
      peppolId: '',
      isActive: 'true'
    });
    setCustomerErrors({});
    setVatSearching(false);
    onClose();
  };

  // VAT Lookup handler
  const handleVATLookup = async () => {
    if (!newCustomerData.vat || newCustomerData.vat.trim() === '') {
      toast.error(t("please_enter_vat_number") || "Please enter a VAT number");
      setCustomerErrors({ ...customerErrors, vat: t("please_enter_vat_number") });
      return;
    }

    // Remove 'BE' prefix if present
    const cleanVAT = newCustomerData.vat.replace(/^BE/i, '').trim();
    
    if (!/^\d+$/.test(cleanVAT)) {
      setCustomerErrors({ ...customerErrors, vat: t("invalid_vat_format") || "Invalid VAT format. Only numbers are allowed" });
      toast.error(t("invalid_vat_format") || "Invalid VAT format. Only numbers are allowed");
      return;
    }

    try {
      setVatSearching(true);
      const response = await lookupCompanyByVAT(cleanVAT);
      const data = response?.payload || response?.data?.payload || response;
      
      if (data && data.Found) {
        // Auto-fill company data from VAT lookup
        setNewCustomerData({
          ...newCustomerData,
          vat: data.EnterpriseNumber || newCustomerData.vat,
          companyName: data.CompanyName || '',
          customerName: data.CompanyName || '', // Also fill customer name
          street: data.StreetAddress || '',
          box: data.Box || '',
          postCode: data.PostCode || '',
          city: data.City || '',
          country: (data.FullAddress && data.FullAddress.includes('BE')) ? 'Belgium' : 'Belgium',
          isActive: data.Status && data.Status.toLowerCase() === 'active' ? 'true' : 'false'
        });
        toast.success(t("company_data_loaded") || "Company data loaded successfully. Please fill remaining fields.");
      } else {
        toast.warning(t("company_not_found") || "No company found with this VAT number");
      }
    } catch (error) {
      console.error('VAT lookup error:', error);
      toast.error(t("vat_lookup_failed") || "Failed to lookup VAT number. Please try again.");
    } finally {
      setVatSearching(false);
    }
  };

  // Create customer handler
  const handleCreateCustomer = async () => {
    try {
      // Validation
      const errors = {};
      
      // VAT - optional if specified in optionalFields
      if (!optionalFields.vat && (!newCustomerData.vat || newCustomerData.vat.trim() === '')) {
        errors.vat = t("vat_required") || "VAT Number is required";
      }
      
      if (!newCustomerData.companyName || newCustomerData.companyName.trim() === '') {
        errors.companyName = t("company_name_required") || "Company Name is required";
      }
      if (!newCustomerData.customerName || newCustomerData.customerName.trim() === '') {
        errors.customerName = t("customer_name_required") || "Customer Name is required";
      }
      
      // Email - optional if specified in optionalFields
      if (!optionalFields.email && (!newCustomerData.email || newCustomerData.email.trim() === '')) {
        errors.email = t("email_required") || "Email is required";
      } else if (newCustomerData.email && newCustomerData.email.trim() !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newCustomerData.email)) {
        errors.email = t("invalid_email") || "Invalid email format";
      }
      
      // Phone - optional if specified in optionalFields
      if (!optionalFields.phone && (!newCustomerData.phoneNumber || newCustomerData.phoneNumber.trim() === '')) {
        errors.phoneNumber = t("phone_required") || "Phone Number is required";
      }
      
      if (!newCustomerData.street || newCustomerData.street.trim() === '') {
        errors.street = t("street_required") || "Street is required";
      }
      if (!newCustomerData.postCode || newCustomerData.postCode.trim() === '') {
        errors.postCode = t("postal_code_required") || "Postal Code is required";
      }
      if (!newCustomerData.city || newCustomerData.city.trim() === '') {
        errors.city = t("city_required") || "City is required";
      }
      
      // Peppol ID validation - Optional but must match format if provided (skip if hidePeppol is true)
      if (!hidePeppol && newCustomerData.peppolId && newCustomerData.peppolId.trim() !== '') {
        const peppolPattern = /^(9925:BE\d+|0208:\d+)$/;
        if (!peppolPattern.test(newCustomerData.peppolId.trim())) {
          errors.peppolId = t("invalid_peppol_format") || "Invalid Peppol ID format. Use 9925:BE1234567890 or 0208:1234567890";
        } else if (newCustomerData.vat && newCustomerData.vat.trim() !== '') {
          // Validate VAT number matches Peppol ID only if VAT is provided
          const cleanVAT = newCustomerData.vat.replace(/^BE/i, '').trim();
          if (newCustomerData.peppolId.startsWith('9925:BE')) {
            const peppolVAT = newCustomerData.peppolId.substring(8); // Remove '9925:BE'
            if (peppolVAT !== cleanVAT) {
              errors.peppolId = t("peppol_vat_mismatch") || "Peppol ID VAT number does not match the entered VAT number";
            }
          } else if (newCustomerData.peppolId.startsWith('0208:')) {
            const peppolVAT = newCustomerData.peppolId.substring(5); // Remove '0208:'
            if (peppolVAT !== cleanVAT) {
              errors.peppolId = t("peppol_vat_mismatch") || "Peppol ID VAT number does not match the entered VAT number";
            }
          }
        }
      }

      if (Object.keys(errors).length > 0) {
        setCustomerErrors(errors);
        return;
      }

      const customerPayload = {
        VATNumber: newCustomerData.vat ? newCustomerData.vat.trim() : '',
        CompanyName: newCustomerData.companyName.trim(),
        Name: newCustomerData.customerName.trim(),
        Email: newCustomerData.email ? newCustomerData.email.trim() : '',
        PhoneNumber: newCustomerData.phoneNumber ? newCustomerData.phoneNumber.trim() : '',
        Street: newCustomerData.street.trim(),
        Box: newCustomerData.box ? newCustomerData.box.trim() : '',
        PostCode: newCustomerData.postCode.trim(),
        City: newCustomerData.city.trim(),
        Country: newCustomerData.country ? newCustomerData.country.trim() : 'Belgium',
        CustomerPeppolId: (!hidePeppol && newCustomerData.peppolId) ? newCustomerData.peppolId.trim() : '',
        Status: newCustomerData.isActive === 'true',
        CreatedDate: new Date().toISOString(),
        IsDeleted: false
      };

      // Call API to create customer
      const response = await addCustomer(customerPayload);
      const createdCustomer = response?.payload || response?.data || response;
      
      if (createdCustomer) {
        // Format customer data for parent component
        const customerForCallback = {
          value: createdCustomer.Id,
          label: createdCustomer.CompanyName || createdCustomer.Name,
          Id: createdCustomer.Id,
          Name: createdCustomer.Name,
          CompanyName: createdCustomer.CompanyName,
          Email: createdCustomer.Email,
          PhoneNumber: createdCustomer.PhoneNumber,
          VATNumber: createdCustomer.VATNumber,
          Street: createdCustomer.Street,
          Box: createdCustomer.Box,
          PostCode: createdCustomer.PostCode,
          City: createdCustomer.City,
          Country: createdCustomer.Country,
          CustomerPeppolId: createdCustomer.CustomerPeppolId,
        };
        
        toast.success(t("customer_created_successfully") || "Customer created successfully!");
        
        // Call parent callback with created customer
        if (onCustomerCreated) {
          onCustomerCreated(customerForCallback);
        }
        
        handleClose();
      }
    } catch (error) {
      console.error('Error creating customer:', error);
      toast.error(error.response?.data?.message || t("error_creating_customer") || "Failed to create customer");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        },
      }}
    >
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          py: 2.5,
        }}
      >
        <PersonIcon sx={{ fontSize: 32 }} />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t("add_new_customer")}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ mt: 3, pb: 2 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="primary" fontWeight="600" sx={{ mb: 2 }}>
            {t("basic_information")}
          </Typography>
          <Grid container spacing={2}>
            {/* VAT Number with Validate button */}
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  label={t("vat_number") + (optionalFields.vat ? '' : ' *')}
                  value={newCustomerData.vat || ''}
                  onChange={(e) => {
                    setNewCustomerData({ ...newCustomerData, vat: e.target.value });
                    if (customerErrors.vat) {
                      setCustomerErrors(prev => ({ ...prev, vat: undefined }));
                    }
                  }}
                  error={Boolean(customerErrors.vat)}
                  helperText={customerErrors.vat || (optionalFields.vat ? t("optional") : t("vat_search_hint")) || ''}
                  sx={{ 
                    flex: 1,
                    '& .MuiOutlinedInput-root': { borderRadius: '10px' } 
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleVATLookup}
                  disabled={vatSearching || !newCustomerData.vat}
                  sx={{
                    minWidth: 90,
                    height: 56,
                    borderRadius: '10px',
                    textTransform: 'none'
                  }}
                >
                  {vatSearching ? <CircularProgress size={20} color="inherit" /> : t("validate")}
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("company_name") + " *"}
                value={newCustomerData.companyName || ''}
                onChange={(e) => {
                  setNewCustomerData({ ...newCustomerData, companyName: e.target.value });
                  if (customerErrors.companyName) {
                    setCustomerErrors(prev => ({ ...prev, companyName: undefined }));
                  }
                }}
                error={Boolean(customerErrors.companyName)}
                helperText={customerErrors.companyName || ' '}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("customer_name") + " *"}
                value={newCustomerData.customerName || ''}
                onChange={(e) => {
                  setNewCustomerData({ ...newCustomerData, customerName: e.target.value });
                  if (customerErrors.customerName) {
                    setCustomerErrors(prev => ({ ...prev, customerName: undefined }));
                  }
                }}
                error={Boolean(customerErrors.customerName)}
                helperText={customerErrors.customerName || ' '}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("email_address") + (optionalFields.email ? '' : ' *')}
                type="email"
                value={newCustomerData.email || ''}
                onChange={(e) => {
                  setNewCustomerData({ ...newCustomerData, email: e.target.value });
                  if (customerErrors.email) {
                    setCustomerErrors(prev => ({ ...prev, email: undefined }));
                  }
                }}
                error={Boolean(customerErrors.email)}
                helperText={customerErrors.email || (optionalFields.email ? t("optional") : ' ')}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("phone_number") + (optionalFields.phone ? '' : ' *')}
                value={newCustomerData.phoneNumber || ''}
                onChange={(e) => {
                  setNewCustomerData({ ...newCustomerData, phoneNumber: e.target.value });
                  if (customerErrors.phoneNumber) {
                    setCustomerErrors(prev => ({ ...prev, phoneNumber: undefined }));
                  }
                }}
                error={Boolean(customerErrors.phoneNumber)}
                helperText={customerErrors.phoneNumber || (optionalFields.phone ? t("optional") : ' ')}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              />
            </Grid>
            {/* Conditionally render Peppol ID field */}
            {!hidePeppol && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t("peppol_id")}
                  value={newCustomerData.peppolId || ''}
                  onChange={(e) => {
                    setNewCustomerData({ ...newCustomerData, peppolId: e.target.value });
                    if (customerErrors.peppolId) {
                      setCustomerErrors(prev => ({ ...prev, peppolId: undefined }));
                    }
                  }}
                  placeholder="9925:BE1234567890 or 0208:1234567890"
                  error={Boolean(customerErrors.peppolId)}
                  helperText={customerErrors.peppolId || t("peppol_id_hint") || "Optional"}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                />
              </Grid>
            )}
          </Grid>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box>
          <Typography variant="subtitle2" color="primary" fontWeight="600" sx={{ mb: 2 }}>
            {t("address_information")}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("street_address") + " *"}
                value={newCustomerData.street || ''}
                onChange={(e) => {
                  setNewCustomerData({ ...newCustomerData, street: e.target.value });
                  if (customerErrors.street) {
                    setCustomerErrors(prev => ({ ...prev, street: undefined }));
                  }
                }}
                error={Boolean(customerErrors.street)}
                helperText={customerErrors.street || ' '}
                multiline
                rows={2}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label={t("box_unit")}
                value={newCustomerData.box || ''}
                onChange={(e) => setNewCustomerData({ ...newCustomerData, box: e.target.value })}
                helperText=" "
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label={t("postal_code") + " *"}
                value={newCustomerData.postCode || ''}
                onChange={(e) => {
                  setNewCustomerData({ ...newCustomerData, postCode: e.target.value });
                  if (customerErrors.postCode) {
                    setCustomerErrors(prev => ({ ...prev, postCode: undefined }));
                  }
                }}
                error={Boolean(customerErrors.postCode)}
                helperText={customerErrors.postCode || ' '}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label={t("city") + " *"}
                value={newCustomerData.city || ''}
                onChange={(e) => {
                  setNewCustomerData({ ...newCustomerData, city: e.target.value });
                  if (customerErrors.city) {
                    setCustomerErrors(prev => ({ ...prev, city: undefined }));
                  }
                }}
                error={Boolean(customerErrors.city)}
                helperText={customerErrors.city || ' '}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}>
                <InputLabel>{t("country")}</InputLabel>
                <Select
                  value={newCustomerData.country || 'Belgium'}
                  label={t("country")}
                  onChange={(e) => setNewCustomerData({ ...newCustomerData, country: e.target.value })}
                >
                  <MenuItem value="Belgium">Belgium</MenuItem>
                  <MenuItem value="Netherlands">Netherlands</MenuItem>
                  <MenuItem value="France">France</MenuItem>
                  <MenuItem value="Germany">Germany</MenuItem>
                  <MenuItem value="Luxembourg">Luxembourg</MenuItem>
                </Select>
                <FormHelperText> </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}>
                <InputLabel>{t("status")}</InputLabel>
                <Select
                  value={newCustomerData.isActive || 'true'}
                  label={t("status")}
                  onChange={(e) => setNewCustomerData({ ...newCustomerData, isActive: e.target.value })}
                >
                  <MenuItem value="true">{t("active")}</MenuItem>
                  <MenuItem value="false">{t("inactive")}</MenuItem>
                </Select>
                <FormHelperText> </FormHelperText>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            textTransform: 'none',
            px: 3,
            borderRadius: 2,
            fontWeight: 600,
          }}
        >
          {t("cancel")}
        </Button>
        <Button
          onClick={handleCreateCustomer}
          variant="contained"
          sx={{
            textTransform: 'none',
            px: 3,
            borderRadius: 2,
            fontWeight: 600,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5568d3 0%, #6a3f91 100%)',
            },
          }}
        >
          {t("create")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddCustomerDialog;
